import axios from "axios";
import { TOKEN_NAME } from "../helper/constants";
import {
  getCookie,
  hideLoader,
  redirectToSamlLogin,
  showLoader
} from "../helper/utils";
import { applyHoFilters, getHoFilterParams } from "./hoFilter";
 
const BASE_URL = `${window.location.origin}/oneapp/api/v1/`;
const BASE_URL2 = `${window.location.origin}/`;
 


// Serialize array params as repeated keys (businesses=a&businesses=b) instead
// of axios's default bracket form (businesses[]=a).
const paramsSerializer = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
    } else {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// Create two axios instances for different base URLs
const client = axios.create({
  baseURL: BASE_URL,
  responseType: "text",
  withCredentials: true,
  timeout: 100000,
  paramsSerializer,
});

const client2 = axios.create({
  baseURL: BASE_URL2,
  responseType: "text/plain",
  withCredentials: true,
  timeout: 100000,
  paramsSerializer,
});
 
let count = 0;
const request = async function (
  {
    method,
    url,
    headers = {},
    data = {},
    responseType = "text/plain",
    params,
    useBaseUrl2 = false,
    silent = false,
  },
  showErrorToast = true
) {
  // Manual (?oneapplogin=true) login stores its token in the TOKEN_NAME cookie.
  // Attach it whenever present so iOS/native login works on deployed envs too,
  // not just localhost. SAML/webgate sessions don't set this cookie.
  const token = getCookie(TOKEN_NAME);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers.isdev = true;
  }
  if (!silent) {
    if (count === 0) showLoader({ show: true });
    count++;
  }
  const onSuccess = function (response) {
    if (!silent) {
      count--;
      if (count === 0) hideLoader();
    }
    return response;
  };
 
  const isUserApi = (url) =>
    url === "user" || (typeof url === "string" && url.endsWith("/user"));
 
  const onError = async function (error) {
    if (!silent) {
      count--;
      hideLoader();
    }

    // Auto-logout for /user 401s. /user is the canonical "is this session
    // valid?" call — it's the FIRST API after SAML and is re-fetched per
    // wrapper mount, so a 401 here always means auth is broken:
    //   - sessionLanded=false: SAML "succeeded" but session cookie didn't
    //     take — kick to /logout-success so the user can retry cleanly
    //     instead of looping back to SAML.
    //   - sessionLanded=true: session expired mid-use — same cleanup path.
    //
    // Every OTHER 401/403 (homeDashboardListingById, siteDetails, FCM
    // register, deactivate-token, etc.) is left to fall through to the
    // normal error toast / Promise.reject so a single transient failure
    // can't kick the user out of the app.
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || "";
      const showLoginPage =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("oneapplogin") === "true";
      const loginPending =
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("login_pending") === "true";
      // Native/iOS credential login must never be sent to SAML or
      // /logout-success from a 401 (same skip 3-feb-prod had for oneapplogin).
      if (!showLoginPage && !loginPending && isUserApi(requestUrl)) {
        const sessionLanded =
          typeof sessionStorage !== "undefined" &&
          sessionStorage.getItem("user_session_landed") === "true";
        const returnedFromSSO =
          typeof sessionStorage !== "undefined" &&
          sessionStorage.getItem("sso_initiated") === "true";
        console.warn(
          "[auth] /user 401. requestUrl=",
          requestUrl,
          "sessionLanded=",
          sessionLanded,
          "sso_initiated=",
          returnedFromSSO
        );
        try {
          const { unregisterFCMToken } = await import("./firebase/notificationService");
          await unregisterFCMToken();
        } catch (e) {}
        // First visit with no session must go to SSO, not /logout-success.
        // After SAML (or a landed session) a 401 still lands on logout-success
        // so we don't loop AuthnRequests.
        if (!sessionLanded && !returnedFromSSO) {
          redirectToSamlLogin("user-401-unauthenticated");
          return;
        }
        window.location.href = "/logout-success";
        return;
      }
    }

    let message = "Something Went Wrong";
 
    let errorMessage;
 
    try{
      errorMessage = JSON.parse(error.response?.data)?.data?.errorMessage;
    }catch(e){
      console.log(e)
      errorMessage = '';
    }
 
    return Promise.reject(errorMessage || message);
  };
 
  // Inject the HO business filter into the body of the endpoints that support
  // org filtering. No-op when nothing is selected or the URL isn't a target.
  const finalData = applyHoFilters(method, url, data);

  // Also pass the selected business as a query param. Caller-supplied params
  // take precedence.
  const mergedParams = {
    ...getHoFilterParams(),
    ...(params || {}),
  };

  const payload = {
    method,
    url,
    headers: {
      ...headers,
    },
    data: finalData,
    params: mergedParams,
    responseType,
  };
 
  const axiosClient = useBaseUrl2 ? client2 : client;
  try {
    const response = await axiosClient(payload);
    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};
 
export default request;